import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import authApi from '../api/auth';

const UserProfile = () => {
  const { user, setUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [description, setDescription] = useState('');
  
  // Password change states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  
  // Profile update states
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  
  // Initialize form when user data is loaded
  React.useEffect(() => {
    if (user) {
      setDescription(user.description || '');
    }
  }, [user]);
  
  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    setProfileSuccess('');
    setProfileError('');
  };
  
  const handlePasswordToggle = () => {
    setIsChangingPassword(!isChangingPassword);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
  };
  
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    try {
      const updatedUser = await authApi.updateProfile({
        description
      });
      
      // Update the user in context
      setUser({
        ...user,
        description: updatedUser.description
      });
      
      setProfileSuccess('Profile updated successfully');
      setProfileError('');
      setIsEditing(false);
    } catch (error) {
      setProfileError('Failed to update profile: ' + (error.response?.data?.detail || error.message));
      setProfileSuccess('');
    }
  };
  
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    
    try {
      await authApi.changePassword(currentPassword, newPassword);
      
      setPasswordSuccess('Password changed successfully');
      setPasswordError('');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setPasswordError('Failed to change password: ' + (error.response?.data?.detail || error.message));
      setPasswordSuccess('');
    }
  };
  
  return (
    <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div className="p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md border border-ui-light-border dark:border-ui-dark-border">
        <h1 className="mb-6 text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">User Profile</h1>
        
        {user ? (
          <div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <h2 className="mb-4 text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Account Information</h2>
                <div className="p-4 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background">
                  <p className="mb-2"><span className="font-medium">Username:</span> {user.username}</p>
                  <p className="mb-2"><span className="font-medium">Email:</span> {user.email}</p>
                  <p className="mb-2"><span className="font-medium">Full Name:</span> {user.full_name || 'Not provided'}</p>
                  <p className="mb-2"><span className="font-medium">Account Type:</span> {
                    user.is_superuser ? 'Superuser' : user.is_staff ? 'Staff' : 'Regular User'
                  }</p>
                </div>
              </div>
              
              <div>
                <h2 className="mb-4 text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Roles and Permissions</h2>
                <div className="p-4 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background">
                  {user.roles && user.roles.length > 0 ? (
                    <div>
                      <p className="mb-2 font-medium">Assigned Roles:</p>
                      <ul className="pl-5 list-disc">
                        {user.roles.map(role => (
                          <li key={role.id} className="text-ui-light-text-secondary dark:text-ui-dark-text-secondary">{role.name}</li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p className="text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">No roles assigned.</p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="mt-6">
              <h2 className="mb-4 text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Profile Description</h2>
              
              {isEditing ? (
                <form onSubmit={handleProfileUpdate} className="p-4 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background">
                  <div className="mb-4">
                    <label htmlFor="description" className="block mb-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                      Description
                    </label>
                    <textarea
                      id="description"
                      rows="4"
                      className="w-full p-2 bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-offset-ui-dark-background transition-colors"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Tell us about yourself..."
                    />
                  </div>
                  
                  {profileError && <p className="mb-4 text-sm text-status-error-light-text dark:text-status-error-dark-text">{profileError}</p>}
                  {profileSuccess && <p className="mb-4 text-sm text-status-success-light-text dark:text-status-success-dark-text">{profileSuccess}</p>}
                  
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="px-4 py-2 text-white bg-primary-600 dark:bg-primary-500 rounded-md hover:bg-primary-700 dark:hover:bg-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-offset-ui-dark-background transition-colors"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={handleEditToggle}
                      className="px-4 py-2 text-ui-light-text-secondary dark:text-ui-dark-text-secondary bg-ui-light-background dark:bg-ui-dark-background border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-offset-ui-dark-background transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="p-4 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background">
                  {user.description ? (
                    <p className="whitespace-pre-wrap text-ui-light-text-secondary dark:text-ui-dark-text-secondary">{user.description}</p>
                  ) : (
                    <p className="text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary italic">No description provided</p>
                  )}
                  <button
                    onClick={handleEditToggle}
                    className="mt-4 px-4 py-2 text-white bg-primary-600 dark:bg-primary-500 rounded-md hover:bg-primary-700 dark:hover:bg-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-offset-ui-dark-background transition-colors"
                  >
                    Edit Description
                  </button>
                </div>
              )}
            </div>
            
            <div className="mt-6">
              <h2 className="mb-4 text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Change Password</h2>
              
              {isChangingPassword ? (
                <form onSubmit={handlePasswordChange} className="p-4 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background">
                  <div className="mb-4">
                    <label htmlFor="currentPassword" className="block mb-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                      Current Password
                    </label>
                    <input
                      id="currentPassword"
                      type="password"
                      className="w-full p-2 bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-offset-ui-dark-background transition-colors"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="newPassword" className="block mb-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                      New Password
                    </label>
                    <input
                      id="newPassword"
                      type="password"
                      className="w-full p-2 bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-offset-ui-dark-background transition-colors"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength="8"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="confirmPassword" className="block mb-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                      Confirm New Password
                    </label>
                    <input
                      id="confirmPassword"
                      type="password"
                      className="w-full p-2 bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-offset-ui-dark-background transition-colors"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>
                  
                  {passwordError && <p className="mb-4 text-sm text-status-error-light-text dark:text-status-error-dark-text">{passwordError}</p>}
                  {passwordSuccess && <p className="mb-4 text-sm text-status-success-light-text dark:text-status-success-dark-text">{passwordSuccess}</p>}
                  
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="px-4 py-2 text-white bg-primary-600 dark:bg-primary-500 rounded-md hover:bg-primary-700 dark:hover:bg-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-offset-ui-dark-background transition-colors"
                    >
                      Change Password
                    </button>
                    <button
                      type="button"
                      onClick={handlePasswordToggle}
                      className="px-4 py-2 text-ui-light-text-secondary dark:text-ui-dark-text-secondary bg-ui-light-background dark:bg-ui-dark-background border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-offset-ui-dark-background transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="p-4 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background">
                  <p className="text-ui-light-text-secondary dark:text-ui-dark-text-secondary">You can change your password here.</p>
                  <button
                    onClick={handlePasswordToggle}
                    className="mt-4 px-4 py-2 text-white bg-primary-600 dark:bg-primary-500 rounded-md hover:bg-primary-700 dark:hover:bg-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-offset-ui-dark-background transition-colors"
                  >
                    Change Password
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-4 text-center">
            <p className="text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Loading user information...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;